import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Boxes,
  Building2,
  ChevronRight,
  Columns3,
  Database,
  LayoutDashboard,
  Layers,
  LogOut,
  Package,
  ScanLine,
  Settings,
  Shield,
  ShieldAlert,
  Tag,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { getMinewESLStatus, logoutBackend, reloginMinewESL } from "@/api/auth";
import {
  apiClient,
  clearImpersonatingTenantId,
  clearToken,
  getRefreshToken,
  getToken,
  setToken,
} from "@/api/client";
import { getTagsOverview } from "@/api/stores";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { SkeletonText } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { decodeJwtClaims } from "@/lib/jwt";
import { cn } from "@/lib/utils";

type BadgeKey = "stores" | "tags";

interface NavItem {
  to: string;
  labelKey: string;
  badge?: BadgeKey;
  icon: LucideIcon;
}

interface NavGroup {
  id: string;
  labelKey: string;
  items: NavItem[];
}

/**
 * Navigacija je grupisana u logičke celine. Grupe su collapsible (state se
 * čuva u localStorage), aktivni link "drži" svoju grupu otvorenu.
 *
 * Logika grupisanja:
 *   • Pregled         — entry tačka
 *   • Retail          — sve što je vezano za podatke u radnji (master data)
 *   • Operacije       — sve što je akcija / sync / monitoring
 *   • Administracija  — korisnici i podešavanja
 */
const NAV_GROUPS: NavGroup[] = [
  {
    id: "overview",
    labelKey: "nav_groups.overview",
    items: [{ to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard }],
  },
  {
    id: "retail",
    labelKey: "nav_groups.retail",
    items: [
      { to: "/stores", labelKey: "nav.stores", badge: "stores", icon: Building2 },
      { to: "/assortment", labelKey: "nav.assortment", icon: Boxes },
      { to: "/schema", labelKey: "nav.schema", icon: Columns3 },
      { to: "/templates", labelKey: "nav.templates", icon: Layers },
      { to: "/products", labelKey: "nav.products", icon: Package },
      { to: "/tags", labelKey: "nav.tags", badge: "tags", icon: Tag },
    ],
  },
  {
    id: "operations",
    labelKey: "nav_groups.operations",
    items: [
      { to: "/onboard/quick", labelKey: "nav.quick_onboard", icon: ScanLine },
      { to: "/sync/datasource", labelKey: "nav.datasource_sync", icon: Database },
      { to: "/operations", labelKey: "nav.operations", icon: Zap },
      { to: "/monitoring", labelKey: "nav.monitoring", icon: Activity },
    ],
  },
  {
    id: "admin",
    labelKey: "nav_groups.admin",
    items: [
      { to: "/users", labelKey: "nav.users", icon: Users },
      { to: "/settings", labelKey: "nav.settings", icon: Settings },
    ],
  },
];

const COLLAPSED_KEY = "eslsync_nav_collapsed_groups";

function useCollapsedGroups(): [Set<string>, (id: string) => void] {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
    return new Set();
  });
  function toggle(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(next)));
      } catch {
        /* ignore */
      }
      return next;
    });
  }
  return [collapsed, toggle];
}

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const qc = useQueryClient();

  const [collapsed, toggleCollapsed] = useCollapsedGroups();

  const claims = decodeJwtClaims(getToken());
  const isSuperAdmin =
    user?.is_super_admin === true || claims?.is_super_admin === true;
  const isImpersonating = claims?.impersonating === true;

  const exitImpersonationMut = useMutation({
    mutationFn: async () => {
      clearImpersonatingTenantId();
      const refresh = getRefreshToken();
      if (!refresh) throw new Error("No refresh token");
      const { data } = await apiClient.post<{ access_token: string; refresh_token: string }>(
        "/api/auth/refresh",
        { refresh_token: refresh },
      );
      setToken(data.access_token);
      if (data.refresh_token) {
        localStorage.setItem("eslsync_refresh_token", data.refresh_token);
      }
    },
    onSuccess: () => {
      qc.clear();
      window.location.assign("/");
    },
  });

  const tagsOverview = useQuery({
    queryKey: ["tags-overview"],
    queryFn: () => getTagsOverview(false),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const minewESLStatus = useQuery({
    queryKey: ["minewesl-status"],
    queryFn: getMinewESLStatus,
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: 1,
  });

  const silentSession = minewESLStatus.data?.silent_session_warning === true;
  const minewESLDown =
    minewESLStatus.data?.connected === false ||
    tagsOverview.data?.upstream_healthy === false ||
    minewESLStatus.isError ||
    tagsOverview.isError ||
    silentSession;
  const bannerHint =
    minewESLStatus.data?.silent_session_hint ??
    minewESLStatus.data?.last_error ??
    (minewESLStatus.error as Error | undefined)?.message ??
    (tagsOverview.error as Error | undefined)?.message ??
    null;

  const reloginMut = useMutation({
    mutationFn: reloginMinewESL,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["minewesl-status"] });
      qc.invalidateQueries({ queryKey: ["tags-overview"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
      qc.invalidateQueries({ queryKey: ["system-goods"] });
      qc.invalidateQueries({ queryKey: ["product-schema"] });
      qc.invalidateQueries({ queryKey: ["schema-fields"] });
    },
  });

  const badges: Record<BadgeKey, number | null> = {
    stores: tagsOverview.data?.stores.length ?? null,
    tags: tagsOverview.data?.total_tags ?? null,
  };

  const upstreamOk =
    minewESLStatus.data?.connected === true &&
    tagsOverview.data?.upstream_healthy !== false;

  async function handleLogout() {
    const refresh = getRefreshToken();
    if (refresh) {
      await logoutBackend(refresh);
    }
    clearToken();
    void navigate("/login", { replace: true });
  }

  function toggleLang() {
    void i18n.changeLanguage(i18n.language === "sr" ? "en" : "sr");
  }

  // Aktivni link "forsira" otvaranje svoje grupe — ako je grupa collapsed a
  // korisnik je na njenom child-u, mora da je vidi.
  const activeGroupId = NAV_GROUPS.find((g) =>
    g.items.some((i) =>
      i.to === "/" ? location.pathname === "/" : location.pathname.startsWith(i.to),
    ),
  )?.id;

  // Inicijala za korisnika (avatar fallback)
  const userInitial = (user?.email ?? "?").charAt(0).toUpperCase();

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* ============================ HEADER ============================ */}
      <header className="flex h-14 shrink-0 items-stretch">
        {/* Brand blok — širina kao sidebar */}
        <div className="relative flex flex-1 shrink-0 items-center gap-3 border-b border-white/[0.06] bg-[#0b0f17] px-4 md:w-64 md:flex-none md:px-5">
          {/* radial glow iza loga */}
          <div
            className="pointer-events-none absolute -left-10 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full opacity-40 blur-2xl"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--primary) / 0.5), transparent 60%)",
            }}
          />
          {theme.logo_url ? (
            <img
              src={theme.logo_url}
              alt={theme.app_name}
              className="relative h-7 object-contain"
            />
          ) : (
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
              <span
                className="absolute inset-0 rounded-md"
                style={{ background: "hsl(var(--primary))" }}
              />
              <span className="relative font-mono text-sm font-bold text-white">
                ⌘
              </span>
            </div>
          )}
          <div className="relative min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold tracking-tight text-white">
              {theme.app_name}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  upstreamOk
                    ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]"
                    : "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]",
                )}
              />
              <span className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">
                {upstreamOk ? "online" : "degraded"}
              </span>
            </div>
          </div>
        </div>

        {/* User kontrole */}
        <div className="flex flex-none items-center justify-end gap-2 border-b border-border bg-card px-3 md:flex-1 md:gap-3 md:px-5">
          <button
            onClick={toggleLang}
            className="rounded-md border border-transparent px-2 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:border-border hover:text-foreground"
          >
            {i18n.language === "sr" ? "EN" : "SR"}
          </button>

          {isSuperAdmin && !isImpersonating && (
            <Link
              to="/admin/tenants"
              title={t("nav.master_console_hint")}
              className="hidden items-center gap-1.5 rounded-md border border-purple-300 bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-900 transition-colors hover:bg-purple-100 sm:inline-flex dark:border-purple-700 dark:bg-purple-950/50 dark:text-purple-200 dark:hover:bg-purple-900/50"
            >
              <Shield size={11} />
              {t("nav.master_console")}
            </Link>
          )}

          <div className="hidden items-center gap-2.5 border-l border-border pl-3 sm:flex">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-white"
              style={{ background: "hsl(var(--primary))" }}
              aria-hidden="true"
            >
              {userInitial}
            </div>
            <div className="min-w-0 leading-tight">
              <div className="truncate text-xs font-medium text-foreground">
                {user?.email ?? "—"}
              </div>
              <div className="truncate font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {isSuperAdmin ? "super-admin" : "tenant user"}
              </div>
            </div>
          </div>

          <button
            onClick={() => void handleLogout()}
            className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/20 hover:bg-accent hover:text-foreground"
          >
            <LogOut size={12} />
            <span className="hidden sm:inline">{t("auth.logout")}</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ============================ SIDEBAR ============================ */}
        <aside className="hidden w-64 flex-col bg-[#0b0f17] md:flex">
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {NAV_GROUPS.map((group, idx) => {
              const isCollapsed =
                collapsed.has(group.id) && activeGroupId !== group.id;
              return (
                <div
                  key={group.id}
                  className={cn("space-y-0.5", idx > 0 && "mt-5")}
                >
                  {group.items.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => toggleCollapsed(group.id)}
                      className="flex w-full items-center gap-1.5 px-3 pb-1.5 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-white/35 transition-colors hover:text-white/60"
                    >
                      <span className="flex-1">{t(group.labelKey)}</span>
                      <ChevronRight
                        size={11}
                        className={cn(
                          "transition-transform",
                          !isCollapsed && "rotate-90",
                        )}
                      />
                    </button>
                  ) : (
                    <div className="px-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                      {t(group.labelKey)}
                    </div>
                  )}

                  {!isCollapsed &&
                    group.items.map((item) => {
                      const isActive =
                        item.to === "/"
                          ? location.pathname === "/"
                          : location.pathname.startsWith(item.to);
                      const badgeValue = item.badge ? badges[item.badge] : null;
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={cn(
                            "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                            isActive
                              ? "bg-white/[0.06] text-white"
                              : "text-white/55 hover:bg-white/[0.03] hover:text-white/85",
                          )}
                        >
                          {isActive && (
                            <span
                              aria-hidden="true"
                              className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
                              style={{ background: "hsl(var(--primary))" }}
                            />
                          )}
                          <Icon
                            size={15}
                            className={cn(
                              "shrink-0 transition-colors",
                              isActive
                                ? "text-[hsl(var(--primary))]"
                                : "text-white/45 group-hover:text-white/75",
                            )}
                          />
                          <span className="flex-1 truncate">
                            {t(item.labelKey)}
                          </span>
                          {badgeValue !== null && (
                            <span
                              className={cn(
                                "rounded-full px-1.5 py-0.5 font-mono text-[10px] tabular-nums leading-none",
                                isActive
                                  ? "bg-white/15 text-white"
                                  : "bg-white/[0.06] text-white/45",
                              )}
                            >
                              {badgeValue}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                </div>
              );
            })}
          </nav>

          {theme.company_name && (
            <div className="border-t border-white/[0.05] px-5 py-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/25">
                {theme.company_name}
              </div>
              <div className="mt-0.5 text-[10px] text-white/20">v0.1.0</div>
            </div>
          )}
        </aside>

        <main className="flex-1 overflow-auto p-3 sm:p-5 md:p-6">
          {isImpersonating && (
            <div className="mb-5 flex items-start gap-3 rounded-md border border-purple-300 bg-purple-50 px-4 py-3 text-sm dark:border-purple-700 dark:bg-purple-950/40">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
              <div className="flex-1">
                <div className="font-medium text-purple-900 dark:text-purple-100">
                  {t("impersonation_banner.title")}
                </div>
                <div className="mt-0.5 text-xs text-purple-800 dark:text-purple-200">
                  {t("impersonation_banner.hint", { tenant_id: claims?.tenant_id ?? "?" })}
                </div>
              </div>
              <button
                type="button"
                disabled={exitImpersonationMut.isPending}
                onClick={() => exitImpersonationMut.mutate()}
                className="ml-2 shrink-0 rounded border border-purple-400 bg-white px-2.5 py-1 text-xs font-medium text-purple-900 hover:bg-purple-50 disabled:opacity-50 dark:border-purple-700 dark:bg-purple-900/40 dark:text-purple-100"
              >
                {t("impersonation_banner.exit")}
              </button>
            </div>
          )}
          {minewESLDown && (
            <div className="mb-5 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-950/40">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="flex-1">
                <div className="font-medium text-amber-900 dark:text-amber-100">
                  {t("minewesl_banner.title")}
                </div>
                <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-200">
                  {bannerHint ?? t("minewesl_banner.hint_default")}
                </div>
              </div>
              <button
                type="button"
                disabled={reloginMut.isPending}
                onClick={() => reloginMut.mutate()}
                className="ml-2 shrink-0 rounded border border-amber-400 bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {reloginMut.isPending
                  ? t("minewesl_banner.cta_reconnecting")
                  : t("minewesl_banner.cta_reconnect")}
              </button>
              <Link
                to="/settings"
                className="shrink-0 rounded border border-amber-400 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
              >
                {t("minewesl_banner.cta_settings")}
              </Link>
              <button
                type="button"
                onClick={() => {
                  void minewESLStatus.refetch();
                  void tagsOverview.refetch();
                }}
                className="shrink-0 rounded border border-amber-400 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
              >
                {t("minewesl_banner.cta_retry")}
              </button>
            </div>
          )}
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<SkeletonText lines={6} className="max-w-xl" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
